import { StackProps, VStack } from '@chakra-ui/react'
import { FieldValidator, useField } from 'formik'
import React, {
  ElementType,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react'
import 'react-phone-number-input/style.css'

import {
  AmhInput,
  AmhProps,
  BinaryInput,
  BinaryInputProps,
  Checkbox,
  CheckboxGroup,
  CheckboxGroupProps,
  CheckboxProps,
  Combobox,
  ComboboxProps,
  CurrencyInput,
  CurrencyProps,
  DateInput,
  DateProps,
  EmailInput,
  HeightInput,
  HeightProps,
  Input,
  InputProps,
  Label,
  NumberInput,
  PasswordInput,
  PasswordProps,
  PhoneInput,
  PhoneProps,
  Placeholders,
  RadioGroup,
  RadioGroupProps,
  Select,
  SelectProps,
  SocialSecurityNumberInput,
  SocialSecurityNumberProps,
  StyledSelect,
  StyledSelectProps,
  Text,
  Textarea,
  TextareaProps,
  WeightInput,
  WeightProps,
  useScrollMarginValue,
} from '@gaia-family/ds'

import { FormManagerContext } from './index'

type FieldInputProps = {
  name: string
  afterInput?: ReactNode
  label?: ReactNode
  validate?: FieldValidator
  helperText?: ReactNode
  disabled?: boolean
  subtitle?: ReactNode
  readonly?: boolean
  errorMessage?: ReactNode
  warningMessage?: ReactNode
  showWarning?: boolean
  wrapProps?: StackProps
  alignItems?: string
  fontSize?: string
  hideInput?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  value?: string
}
type FieldProps<T> = T & FieldInputProps
type Options = {
  useEventAPI?: boolean
  ReadonlyComponent?: ElementType
}

const getErrorMessage = (error: string | object) => {
  if (typeof error === 'string') return error

  const [schemaMessage] = Object.values(error).filter(Boolean)
  return schemaMessage
}

export const ErrorComponent = ({
  errorMessage,
  error,
}: {
  errorMessage?: ReactNode
  error?: string
}) => (
  <Text size="xs" textStyle="cta" color="failRed">
    {errorMessage ?? (error ? getErrorMessage(error) : null)}
  </Text>
)

const makeFieldComponent = function <T>(
  InputComponent: ElementType,
  { useEventAPI = true, ReadonlyComponent }: Options = {}
) {
  const FieldComponent = ({
    name,
    validate,
    label,
    helperText,
    subtitle,
    disabled,
    readonly,
    warningMessage,
    showWarning,
    errorMessage,
    afterInput,
    wrapProps = {},
    alignItems = 'flex-start',
    fontSize = 'sm',
    hideInput = false,
    ...props
  }: FieldProps<T>) => {
    const [
      { onChange, ...field },
      { error, touched },
      { setValue, setTouched },
    ] = useField({
      name,
      validate,
    })
    const { register } = useContext(FormManagerContext)
    const fieldRef = useRef<HTMLDivElement>(null)
    const scrollMargin = useScrollMarginValue()

    const fieldProps = {
      onChange: useEventAPI ? onChange : setValue,
      isDisabled: disabled,
      isInvalid: Boolean(touched && error),
      setTouched,
      ...field,
      ...props,
    }

    useEffect(() => {
      register(name, fieldRef)
      // todo: fix this
      // eslint-disable-next-line
    }, [])

    if (!ReadonlyComponent && readonly) {
      throw new TypeError(
        'Field is "readonly" but has no "ReadonlyComponent" defined. Please define one'
      )
    }

    const RenderedComponent =
      readonly && ReadonlyComponent ? ReadonlyComponent : InputComponent

    return (
      <VStack
        spacing="xs"
        alignItems={alignItems}
        ref={fieldRef}
        style={{
          scrollMarginTop: scrollMargin.top,
        }}
        {...wrapProps}
      >
        {(label || helperText) && (
          <Label
            label={label}
            subtitle={hideInput ? undefined : subtitle}
            helperText={helperText}
            size={fontSize}
            zIndex={1}
          />
        )}
        {!hideInput && <RenderedComponent {...fieldProps} />}

        {afterInput}
        {touched && error ? (
          <ErrorComponent error={error} errorMessage={errorMessage} />
        ) : showWarning ? (
          <Text size="xs" textStyle="cta" color="orange">
            {warningMessage}
          </Text>
        ) : null}
      </VStack>
    )
  }
  FieldComponent.displayName = `Field.${
    // @ts-ignore
    InputComponent.displayName || 'Unknown'
  }`
  return FieldComponent
}

type MadeProps<T> = Omit<T, 'onChange' | 'value'>

export const Fields = {
  Text: makeFieldComponent<MadeProps<InputProps>>(Input, {
    ReadonlyComponent: Placeholders.Text,
  }),
  Textarea: makeFieldComponent<MadeProps<TextareaProps>>(Textarea, {
    ReadonlyComponent: Placeholders.Text,
  }),
  Checkbox: makeFieldComponent<MadeProps<CheckboxProps>>(Checkbox),
  Email: makeFieldComponent<MadeProps<InputProps>>(EmailInput, {
    ReadonlyComponent: Placeholders.Text,
  }),
  Password: makeFieldComponent<MadeProps<PasswordProps>>(PasswordInput),
  SocialSecurityNumber: makeFieldComponent<
    MadeProps<SocialSecurityNumberProps>
  >(SocialSecurityNumberInput),
  Weight: makeFieldComponent<MadeProps<WeightProps>>(WeightInput, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Weight,
  }),
  Height: makeFieldComponent<MadeProps<HeightProps>>(HeightInput, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Height,
  }),
  Amh: makeFieldComponent<MadeProps<AmhProps>>(AmhInput, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Amh,
  }),
  Number: makeFieldComponent<MadeProps<InputProps>>(NumberInput, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Number,
  }),
  Select: makeFieldComponent<MadeProps<SelectProps>>(Select, {
    ReadonlyComponent: Placeholders.Select,
  }),
  Combobox: makeFieldComponent<MadeProps<ComboboxProps>>(Combobox, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Select,
  }),
  StyledSelect: makeFieldComponent<MadeProps<StyledSelectProps>>(StyledSelect, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Select,
  }),
  Date: makeFieldComponent<MadeProps<DateProps>>(DateInput, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.Date,
  }),
  CheckboxGroup: makeFieldComponent<MadeProps<CheckboxGroupProps>>(
    CheckboxGroup,
    {
      useEventAPI: false,
      ReadonlyComponent: Placeholders.CheckboxGroup,
    }
  ),
  RadioGroup: makeFieldComponent<MadeProps<RadioGroupProps>>(RadioGroup, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.RadioGroup,
  }),
  BinaryInput: makeFieldComponent<MadeProps<BinaryInputProps>>(BinaryInput, {
    useEventAPI: false,
    ReadonlyComponent: Placeholders.BinaryInput,
  }),
  Currency: makeFieldComponent<MadeProps<CurrencyProps>>(CurrencyInput, {
    useEventAPI: false,
  }),
  PhoneInput: makeFieldComponent<MadeProps<PhoneProps>>(PhoneInput, {
    ReadonlyComponent: Placeholders.Text,
    useEventAPI: false,
  }),
}
